import { ChangeDetectionStrategy, Component, ElementRef, inject, input, OnInit, PLATFORM_ID, signal } from '@angular/core';
import { CommonModule, isPlatformBrowser, DOCUMENT } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router, RouterModule } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { filter } from 'rxjs';
import { Role } from 'src/app/models/subscription-info.model';
import { User } from 'src/app/models/user/user.model';
import { LogOutFreeServices } from 'src/app/shared/services/log-out.service ';
import { HeaderCountriesDropdownComponent } from '../../latest-header/header-countries-dropdown/header-countries-dropdown.component';

@Component({
   selector: 'app-user-dropdown',
   templateUrl: './user-dropdown.component.html',
   styleUrls: ['./user-dropdown.component.scss'],
   standalone: true,
   imports: [CommonModule, HeaderCountriesDropdownComponent, RouterModule],
   changeDetection: ChangeDetectionStrategy.OnPush,
   host: {
      '(document:click)': 'clickOutside($event)'
   }
})
export class UserDropdownComponent implements OnInit {
   private readonly router = inject(Router);
   private readonly logoutService = inject(LogOutFreeServices);
   private readonly modalService = inject(NgbModal);
   private readonly el = inject(ElementRef);
   private readonly platformId = inject(PLATFORM_ID);
   private readonly isBrowser = isPlatformBrowser(this.platformId);

   readonly user = input<User | null>(null);
   readonly userLoading = input(false);
   readonly userError = input<string | null>(null);
   readonly trialNotClaimed = input(false);
   readonly showQuestionnaireReward = input(false);
   readonly userNewRole = input<Role | null>(null);

   readonly isUserDropdownOpen = signal(false);
   readonly isCountryChangeOptionVisible = signal(false);

   private readonly visibleRoutesForCountryChangeOption: string[] = [
      '/',
      '/trending-halal-stocks/most_active',
      '/trending-halal-stocks/most_popular',
      '/trending-halal-stocks/top_gainers',
      '/trending-halal-stocks/top_losers',
      '/trending-halal-stocks/52_week_high',
      '/trending-halal-stocks/52_week_low',
      '/halal-stock-collections/information_technology',
      '/halal-stock-collections/health_care',
      '/halal-stock-collections/consumer_discretionary',
      '/halal-stock-collections/mega_cap',
      '/halal-stock-collections/large_cap',
      '/halal-stock-collections/mid_cap',
      '/halal-stock-collections/small_cap',
   ];

   constructor() {
      // React to route changes
      this.router.events
         .pipe(
            filter((event): event is NavigationEnd => event instanceof NavigationEnd),
            takeUntilDestroyed()
         )
         .subscribe(event => {
            if (!this.isBrowser) return;
            // Defer signal write to avoid NG0600
            setTimeout(() => this.checkVisibility(event.urlAfterRedirects), 0);
         });
   }

   ngOnInit(): void {
      this.checkVisibility(this.router.url);
   }

   private checkVisibility(url: string): void {
      const currentUrl = this.normalizeUrl(url);
      this.isCountryChangeOptionVisible.set(this.visibleRoutesForCountryChangeOption.some(
         route => this.normalizeUrl(route) === currentUrl
      ));
   }

   private normalizeUrl(url: string): string {
      if (!url) return '/';
      let clean = url.split('?')[0].split('#')[0].replace(/\/+$/, '');
      return clean === '' ? '/' : clean;
   }

   logout() {
      this.closeUserDropdown();
      this.logoutService.show();
   }

   navigateTo(path: string) {
      this.closeUserDropdown();
      this.router.navigate([path]);
   }

   goToInvesterDashboard() {
      this.closeUserDropdown();
      this.router.navigate(['/investor-relations'], {
         queryParams: { tab: 'investor-dashboard' },
      });
   }

   toggleUserDropdown() {
      this.isUserDropdownOpen.update(v => !v);
   }

   closeUserDropdown() {
      this.isUserDropdownOpen.set(false);
   }

   clickOutside(event: Event) {
      const clickedInside = this.el.nativeElement.querySelector('.user-dropdown')?.contains(event.target as Node);
      if (!clickedInside) {
         this.closeUserDropdown();
      }
   }
}